import { useEffect, useRef, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSnackbar } from '../../helpers/hooks/useSnackbar'
import {
  readChatRoomFirebase,
  updateChatRoomFirebase,
  updateChatAssigneeFirebase,
} from '../../firebase/privateChat/firebaseActions'
import { cutTooLong } from '../../helpers/helpers'
import API from '../../api/api'

import {
  convertMessageProperly,
  removeDuplicate,
  isHighManagementUser,
  getReverseProperTeamType,
  getChatTypeFromConversation,
  getSpecificChatTypeFromConversation,
  createFormData,
} from './helpers'
import { TwilioService } from './services/twilio-service'

const useChatMessages = ({
  selectedChat,
  availableManagementUsers,
  currentUser,
  sendMessageAsSMS,
  selectedBuildingId,
  cancelLoadingMessages,
  highManagementUsers,
  scrollToBottom,
}) => {
  const [isLoadedAll, setIsLoadedAll] = useState(false)
  const [allMessages, setAllMessages] = useState([])
  const [counterLastMessagesStatusToChange, setCounterLastMessagesStatusToChange] = useState(0)
  const [smsMessagesLeft, setSmsMessagesLeft] = useState(null)

  const chatMessagesPaginator = useRef()
  // Resolves to the selected chat's Twilio conversation (null if its load failed or was
  // cancelled), so a send made while the chat is still loading waits instead of failing.
  const pendingChatConversation = useRef(Promise.resolve(null))
  const { displaySnackbar } = useSnackbar()
  const { t } = useTranslation(['chat'])

  const currentUserId = currentUser.user.id
  const currentUserRole = currentUser.user.role
  const selectedChatChannelName = selectedChat ? selectedChat.channelName : null

  const getProperIdsFromRoles = (currentUserRole, chatType) => {
    const targetRole = isHighManagementUser(currentUserRole)
      ? getReverseProperTeamType(chatType)
      : currentUserRole

    return availableManagementUsers
      .filter(
        (managementUser) =>
          currentUserRole === 'PropertyManager' ||
          (managementUser.user
            ? managementUser.user.role === targetRole
            : managementUser.role === targetRole),
      )
      .map((managementUser) => (managementUser.user ? managementUser.user.id : managementUser.id))
  }

  const highAndNormalManagementUsers = highManagementUsers
    ? [...highManagementUsers, ...availableManagementUsers]
    : availableManagementUsers
  const chatType = getChatTypeFromConversation(selectedChat)
  const teamTypeUserIds = getProperIdsFromRoles(currentUserRole, chatType)
  const chatClientID = selectedChatChannelName
    ? selectedChatChannelName.slice(0, selectedChatChannelName.indexOf('_'))
    : null

  const convertPage = (paginator) =>
    Promise.all(
      TwilioService.getInstance()
        .parseMessages(paginator.items)
        .map((message) =>
          convertMessageProperly(message, selectedChat, highAndNormalManagementUsers),
        ),
    )

  // Page backwards through the paginator from the initial load. Don't compute an anchor from
  // getMessagesCount(): the count is only eventually consistent and message indices can have
  // gaps, so a count-based anchor skips messages.
  const fetchMoreMessages = async () => {
    const paginator = chatMessagesPaginator.current
    if (!paginator?.hasPrevPage) return
    try {
      const prevPaginator = await paginator.prevPage()
      const convertedMessages = await convertPage(prevPaginator)
      // Chat switched (paginator is reset), or a concurrent call already advanced past this page.
      if (chatMessagesPaginator.current !== paginator) return
      chatMessagesPaginator.current = prevPaginator
      setIsLoadedAll(!prevPaginator.hasPrevPage)
      setAllMessages((prevMessages) => [...convertedMessages, ...prevMessages])
    } catch (err) {
      console.error(err.message)
    }
  }

  const orderedMessages = useMemo(() => {
    const updatedMessages = [...allMessages]
    if (updatedMessages.length) {
      const searchableSMSindex = updatedMessages.findIndex(
        (message) => message.messageStatus === 'sending',
      )
      if (searchableSMSindex !== -1 && counterLastMessagesStatusToChange > 0) {
        updatedMessages[searchableSMSindex].messageStatus = 'error'
        // TODO: uncomment resolve error
        // eslint-disable-next-line react-hooks/set-state-in-render
        setCounterLastMessagesStatusToChange(counterLastMessagesStatusToChange - 1)
      }
    }
    return removeDuplicate(updatedMessages).sort(
      (prev, next) => new Date(next.createdAt).getTime() - new Date(prev.createdAt).getTime(),
    )
  }, [allMessages, counterLastMessagesStatusToChange])

  const isSendByUs = (message) => {
    if (message.user._id === 'system') {
      return true
    }

    if (isHighManagementUser(currentUserRole)) {
      return currentUserId === Number(message.user._id)
    }
    return teamTypeUserIds.includes(Number(message.user._id))
  }

  const updateChatPreview = (lastMessage) => {
    updateChatRoomFirebase(
      selectedBuildingId,
      selectedChatChannelName,
      Number(chatClientID),
      Number(currentUserId),
      lastMessage,
    )
    if (['PropertyManager', 'ShiftManager', 'Staff'].includes(currentUserRole)) {
      updateChatAssigneeFirebase(selectedBuildingId, selectedChatChannelName, Number(currentUserId))
    }
  }

  const onSend = async (text) => {
    if (sendMessageAsSMS) {
      try {
        await API.createSMSmessage({ message: text, conversation: selectedChatChannelName })
        setSmsMessagesLeft(smsMessagesLeft - 1)
      } catch (err) {
        setCounterLastMessagesStatusToChange(counterLastMessagesStatusToChange + 1)
        displaySnackbar(err.response.data.message, true)
        throw new Error(err)
      }
    } else {
      const attributes = {
        giftedId: currentUserId.toString(),
      }
      const conversation = await pendingChatConversation.current
      if (!conversation) {
        displaySnackbar(t('chat:genericErrors.somethingWentWrong'), true)
        return
      }
      try {
        await conversation.sendMessage(text, attributes)
      } catch (err) {
        console.error(err.message)
        displaySnackbar(t('chat:genericErrors.somethingWentWrong'), true)
        return
      }
      updateChatPreview(cutTooLong(text, 35))
    }
  }

  const onSendWithAttachements = async (files) => {
    if (!files.length) return null
    const attributes = {
      giftedId: currentUserId.toString(),
    }
    const conversation = await pendingChatConversation.current
    if (!conversation) {
      displaySnackbar(t('chat:genericErrors.somethingWentWrong'), true)
      return null
    }

    const results = await Promise.allSettled(
      files.map((file) =>
        Promise.resolve().then(() => conversation.sendMessage(createFormData(file), attributes)),
      ),
    )
    const failed = results.filter((result) => result.status === 'rejected')
    if (failed.length) {
      failed.forEach((result) => console.error(result.reason?.message))
      displaySnackbar(t('chat:genericErrors.somethingWentWrong'), true)
    }
    // Only skip the preview update if nothing was delivered.
    if (failed.length === results.length) return null
    updateChatPreview('[Image]')
  }

  const handleRemoveLocalMessageById = (messageId) => {
    const updatedMessages = allMessages.filter((message) => message._id !== messageId)
    setAllMessages(updatedMessages)
  }

  const onReSendSMS = async ({ text, _id }) => {
    handleRemoveLocalMessageById(_id)
    try {
      await API.createSMSmessage({
        message: text,
        conversation: selectedChatChannelName,
        resendMessageSid: _id,
      })
      setSmsMessagesLeft(smsMessagesLeft - 1)
    } catch (err) {
      setCounterLastMessagesStatusToChange(counterLastMessagesStatusToChange + 1)
      displaySnackbar(err.response.data.message, true)
      throw new Error(err)
    }
  }

  const convertAndSetMessages = async (message, isCancelled) => {
    const newMessage = TwilioService.getInstance().parseMessage(message)
    const convertedMessage = await convertMessageProperly(
      newMessage,
      selectedChat,
      highAndNormalManagementUsers,
    )
    if (isCancelled()) return
    const { giftedId } = message.attributes
    if (!giftedId || message.author !== currentUserId) {
      setAllMessages((prevMessages) => [convertedMessage, ...prevMessages])
    } else {
      setAllMessages((prevMessages) =>
        prevMessages.map((m) => (m._id === giftedId ? convertedMessage : m)),
      )
    }
    scrollToBottom?.()
  }

  const [reconnectCount, setReconnectCount] = useState(0)

  useEffect(
    () => TwilioService.getInstance().onReconnect(() => setReconnectCount((count) => count + 1)),
    [],
  )

  useEffect(() => {
    const teamType = getSpecificChatTypeFromConversation(selectedChat)
    const dataAboutChat = {
      teamType: selectedChat.teamType || teamType,
      conversationUserId: Number(chatClientID),
    }
    if (teamType === 'MTRQ') {
      dataAboutChat.requestId = Number(selectedChat.channelName.split('_')[2])
    }
    if (teamType.startsWith('CUST')) {
      dataAboutChat.customInboxId = Number(selectedChat.channelName.split('_')[2])
    }

    // Set by the cleanup on chat switch or unmount; every async step below checks it so nothing
    // from this load (messages, paging, the listener) leaks into the next chat.
    let isCancelled = false
    let listenedConversation
    const onMessageAdded = (message) => convertAndSetMessages(message, () => isCancelled)

    chatMessagesPaginator.current = undefined
    setIsLoadedAll(false)
    setAllMessages([])

    const conversationLoad = API.createOrJoinConversation(dataAboutChat)
      .then(() => TwilioService.getInstance().getConversationsClient())
      .then((client) => client.getConversationByUniqueName(selectedChatChannelName))
      .then((conversation) => {
        if (isCancelled) throw new Error('Chat load cancelled')
        listenedConversation = conversation
        conversation.addListener('messageAdded', onMessageAdded)
        return conversation
      })
    pendingChatConversation.current = conversationLoad.catch(() => null)

    conversationLoad
      // No anchor: fetch the newest page. A getMessagesCount()-based anchor cuts off the
      // latest messages whenever the count lags behind or indices have gaps (EL-13944).
      .then((conversation) => conversation.getMessages(15))
      .then(async (paginator) => {
        const convertedMessages = await convertPage(paginator)
        if (isCancelled) return
        chatMessagesPaginator.current = paginator
        setIsLoadedAll(!paginator.hasPrevPage)
        // Merge rather than replace: messageAdded may already have delivered a live message
        // while this page was loading. orderedMessages de-duplicates by id.
        setAllMessages((prevMessages) => [...convertedMessages, ...prevMessages])
      })
      .catch((err) => {
        if (!isCancelled) console.error(err.message)
      })
      .finally(() => {
        // Always clear the shared loading flag (as before) so an unmount mid-load can't leave
        // it stuck; only mark read / scroll for a chat that's still shown.
        cancelLoadingMessages()
        if (isCancelled) return
        readChatRoomFirebase(selectedBuildingId, selectedChatChannelName)
        scrollToBottom?.()
      })

    return () => {
      isCancelled = true
      listenedConversation?.removeListener('messageAdded', onMessageAdded)
    }
  }, [selectedChatChannelName, reconnectCount])

  return {
    isLoadedAll,
    orderedMessages,
    smsMessagesLeft,
    isSendByUs,
    fetchMoreMessages,
    onSend,
    onReSendSMS,
    onSendWithAttachements,
    setSmsMessagesLeft,
  }
}

export default useChatMessages
